import { appLocale, t } from "./i18n";
import { buildApiUrl, runtimeConfig, type RuntimeMarketConfig } from "./runtime-config";

export type AuthProviderKey = "google" | "wechat";

export interface AuthProviderEntry {
  enabled: boolean;
  label: string;
  startUrl: string;
}

export interface AuthProviderSettings {
  google: AuthProviderEntry;
  wechat: AuthProviderEntry;
}

export interface LoginProviderOption extends AuthProviderEntry {
  key: AuthProviderKey;
}

export interface LoginOptions {
  providers: LoginProviderOption[];
  showPasswordLogin: boolean;
}

export interface WechatEmbeddedLoginConfig {
  appId: string;
  scope: string;
  redirectUri: string;
  state: string;
  style: string;
  colorScheme: string;
}

export interface WechatEmbeddedLoginResponse {
  token: string;
}

export const defaultAuthProviderSettings: AuthProviderSettings = {
  google: {
    enabled: false,
    label: t("loginProviders.google"),
    startUrl: "/api/auth/google/start"
  },
  wechat: {
    enabled: false,
    label: t("loginProviders.wechat"),
    startUrl: "/api/auth/wechat/start"
  }
};

export function resolveLoginOptions(settings: AuthProviderSettings, market: RuntimeMarketConfig): LoginOptions {
  const providers: LoginProviderOption[] = [];

  if (settings.google.enabled && market.showGoogleLogin) {
    providers.push({
      key: "google",
      ...settings.google,
      label: localizedProviderLabel("google", market)
    });
  }

  if (settings.wechat.enabled && market.showWechatLogin) {
    providers.push({
      key: "wechat",
      ...settings.wechat,
      label: localizedProviderLabel("wechat", market)
    });
  }

  return {
    providers,
    // 邮箱密码登录入口暂时下线；第三方配置异常时也不再回退展示旧表单。
    showPasswordLogin: false
  };
}

function localizedProviderLabel(provider: AuthProviderKey, market: RuntimeMarketConfig): string {
  if (provider === "google") {
    return market.isInternationalVersion ? "Sign in with Google" : "Google 登录";
  }

  return market.isChinaVersion ? "微信登录" : "Sign in with WeChat";
}

export function resolveProviderStartUrl(startUrl: string, apiBaseUrl: string): string {
  const value = startUrl.trim();
  if (/^https?:\/\//i.test(value)) {
    return value;
  }
  const normalizedPath = value.startsWith("/") ? value : `/${value}`;
  return `${apiBaseUrl.replace(/\/+$/, "")}${normalizedPath}`;
}

export async function fetchAuthProviderSettings(): Promise<AuthProviderSettings> {
  try {
    const response = await fetch(buildApiUrl("/api/auth/providers"), {
      headers: {
        "X-App-Locale": appLocale,
        "X-App-Region": runtimeConfig.market.region
      }
    });
    const json = await response.json().catch(() => null);
    if (!response.ok) {
      throw new Error(json?.message || t("loginProviders.loadFailed"));
    }
    return normalizeAuthProviderSettings(json);
  } catch {
    return defaultAuthProviderSettings;
  }
}

export async function fetchWechatEmbeddedLoginConfig(returnUrl: string): Promise<WechatEmbeddedLoginConfig> {
  const response = await fetch(buildApiUrl(`/api/auth/wechat/embedded-config?returnUrl=${encodeURIComponent(returnUrl)}`), {
    headers: {
      "X-App-Locale": appLocale,
      "X-App-Region": runtimeConfig.market.region
    }
  });
  const json = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(json?.message || t("loginProviders.loadFailed"));
  }
  return json as WechatEmbeddedLoginConfig;
}

export async function completeWechatEmbeddedLogin(code: string, state: string): Promise<WechatEmbeddedLoginResponse> {
  const response = await fetch(buildApiUrl("/api/auth/wechat/embedded-complete"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-App-Locale": appLocale,
      "X-App-Region": runtimeConfig.market.region
    },
    body: JSON.stringify({ code, state })
  });
  const json = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(json?.message || t("login.loginFailed"));
  }
  return json as WechatEmbeddedLoginResponse;
}

function normalizeAuthProviderSettings(value: unknown): AuthProviderSettings {
  const settings = value as Partial<AuthProviderSettings> | null;
  return {
    google: normalizeProvider(settings?.google, defaultAuthProviderSettings.google),
    wechat: normalizeProvider(settings?.wechat, defaultAuthProviderSettings.wechat)
  };
}

function normalizeProvider(value: Partial<AuthProviderEntry> | undefined, fallback: AuthProviderEntry): AuthProviderEntry {
  return {
    enabled: Boolean(value?.enabled),
    label: typeof value?.label === "string" && value.label.trim() ? value.label.trim() : fallback.label,
    startUrl: typeof value?.startUrl === "string" && value.startUrl.trim() ? value.startUrl.trim() : fallback.startUrl
  };
}
